import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete, Box, Chip, TextField, Typography,
    Stack
} from '@mui/material';
import * as api from './subCategoriesApi';
import type { SubCategory, MainGroup, Intrastat } from './subCategoriesApi';
import { useDetailView } from '../common/useDetailView';
import { sx14, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    subCategoryId: number | null;
}

export function SubCategoryView({ subCategoryId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<SubCategory>();

    const [mainGroups, setMainGroups] = useState<MainGroup[]>([]);
    const [intrastats, setIntrastats] = useState<Intrastat[]>([]);

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const [mgs, intr] = await Promise.all([api.getMainGroups(), api.getIntrastats()]);
            setMainGroups(mgs);
            setIntrastats(intr);
            if (subCategoryId === null) {
                detail.setItem({ id: 0, subCategoryCode: '', subCategoryName: '', mainGroups: [], intrastats: [] });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getSubCategory(subCategoryId);
                data.mainGroups = data.mainGroups ?? [];
                data.intrastats = data.intrastats ?? [];
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [subCategoryId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = await api.saveSubCategory(item);
            response.mainGroups = response.mainGroups ?? [];
            response.intrastats = response.intrastats ?? [];
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    return (
        <DetailViewShell<SubCategory>
            title={`${item?.subCategoryCode ?? ''} ${item?.subCategoryName ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={
                !!item &&
                !!item.subCategoryCode &&
                item.subCategoryCode.length >= 1 &&
                item.subCategoryCode.length <= 3 &&
                !!item.subCategoryName
            }
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(subCategory) => {
                const isCodeValid = !!subCategory.subCategoryCode && subCategory.subCategoryCode.length >= 1 && subCategory.subCategoryCode.length <= 3;
                const isNameValid = !!subCategory.subCategoryName;

                return (<>
                    <FormRow label={t('subCategory.subCategoryCode')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{subCategory.subCategoryCode}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                inputProps={{ maxLength: 3, minLength: 1 }}
                                value={subCategory.subCategoryCode}
                                onChange={e => detail.update('subCategoryCode', e.target.value)}
                                error={!isCodeValid}
                                helperText={!isCodeValid ? t('subCategory.betweenValues') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('subCategory.subCategoryName')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx14}>{subCategory.subCategoryName}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={subCategory.subCategoryName}
                                onChange={e => detail.update('subCategoryName', e.target.value)}
                                error={!isNameValid}
                                helperText={!isNameValid ? t('subCategory.required') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('subCategory.maingroups')}>
                        {!detail.isEditing ? (
                            <Box>
                                {[...subCategory.mainGroups]
                                    .sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode))
                                    .map(mg => (
                                        <Typography sx={sx14} key={mg.id}>
                                            {mg.mainGroupCode} {mg.mainGroupDescription}
                                        </Typography>
                                    ))}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={[...mainGroups].sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode))}
                                getOptionLabel={o => `${o.mainGroupCode} ${o.mainGroupDescription}`}
                                value={subCategory.mainGroups}
                                onChange={(_, value) => detail.update('mainGroups', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} placeholder={t('subCategory.maingroups')} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((mg, i) => (
                                        <Chip {...getTagProps({ index: i })} key={mg.id}
                                            label={`${mg.mainGroupCode} ${mg.mainGroupDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>

                    {intrastats.length > 0 && (
                        <FormRow label={t('maingroup.intrastatnumber')}>
                            {!detail.isEditing ? (
                                <Box>
                                    {subCategory.intrastats.map(i => (
                                        <Typography sx={sx14} key={i.id} title={i.internalDescription}>
                                            {i.code}
                                        </Typography>
                                    ))}
                                </Box>
                            ) : (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={[...intrastats].sort((a, b) => a.code.localeCompare(b.code))}
                                    getOptionLabel={o => `${o.code} ${o.internalDescription}`}
                                    value={subCategory.intrastats}
                                    onChange={(_, value) => detail.update('intrastats', value)}
                                    isOptionEqualToValue={(o, v) => o.id === v.id}
                                    renderInput={params => <TextField {...params} />}
                                    renderTags={(value, getTagProps) =>
                                        value.map((i, idx) => (
                                            <Chip {...getTagProps({ index: idx })} key={i.id}
                                                label={`${i.code} ${i.internalDescription}`} size="small" />
                                        ))
                                    }
                                />
                            )}
                        </FormRow>
                    )}
                </>);
            }}
        </DetailViewShell>
    );
}
